import type { FeatureFlagT } from '../proctor/FeatureFlags';
import type { QualificationAttribute } from '../types/JobAttribute';

import Sentiment from '../../../../../../shared/util/salary/Sentiment';
import JobTagStrictness from '../requirements/JobTagStrictness';
import {
    type default as TaxonomyEntity,
    InferenceMethod,
    Source,
    Status,
} from '../requirements/TaxonomyEntity';
import YellowLightCategoryType, {
    getCategoryTypeIdFromRawType,
} from '../types/YellowLightCategoryType';

const customClassIdToYellowLightCategoryType = {
    Q2W6Y: YellowLightCategoryType.SKILLS,
    ZXX9P: YellowLightCategoryType.CERTIFICATIONS,
    Y7ER9: YellowLightCategoryType.LICENSE,
    NP7FU: YellowLightCategoryType.EDUCATION,
    D3R7V: YellowLightCategoryType.LANGUAGES,
    // This custom class is actually Government Security Clearances, but we display those as licenses currently.
    '5ZKTQ': YellowLightCategoryType.LICENSE,
};

export const EXPERIMENTAL_CHIP_LIMIT = 30;

type GetJobAttributesInput = {
    attributeComparisons: component.oneGraphMatchComparison.AttributeComparison[];
    taxonomyEntities: TaxonomyEntity[];
    jobTagReqIdsToStrictness?: {
        [key: string]: JobTagStrictness;
    };
    taxonomyIdToJssdId?: {
        [key: string]: string;
    };
};

function getSentiment(source?: Source, status?: Status): Sentiment {
    switch (status) {
        case Status.NEGATIVE_MATCH:
            return Sentiment.NEGATIVE;
        case Status.MATCHED:
            return Sentiment.POSITIVE;
        default:
            return source === Source.BOTH ? Sentiment.POSITIVE : Sentiment.UNKNOWN;
    }
}

function getMCESentiment(matchType?: jobseekerapi.type.MatchComparisonEngineMatchType): Sentiment {
    switch (matchType) {
        case 'MATCH':
            return Sentiment.POSITIVE;
        case 'MISMATCH':
            return Sentiment.NEGATIVE;
        case 'JOB_ONLY':
        case 'JS_ONLY':
        default:
            return Sentiment.UNKNOWN;
    }
}

function getInferenceMethod(
    jsProvenance?: jobseekerapi.type.MatchComparisonEngineProvenance,
): InferenceMethod {
    switch (jsProvenance) {
        case 'CONFIRMED':
            return InferenceMethod.DIRECT;
        case 'EXTRACTED':
            return InferenceMethod.EXTRACTED;
        case 'BEHAVIORAL_INFERRED':
        case 'HIGH_CONFIDENCE_DEDUCED_FROM_CONFIRMED':
        case 'HIGH_CONFIDENCE_DEDUCED_FROM_EXTRACTED':
            return InferenceMethod.STRONGLY_INFERRED;
        case 'NOT_PRESENT':
        default:
            return InferenceMethod.NONE;
    }
}

function getStrictness(
    shouldHideStrictnessForInferredAttributes: boolean,
    jobRequirementStrength?: jobseekerapi.type.MatchComparisonEngineRequirementStrength,
    jobProvenance?: jobseekerapi.type.MatchComparisonEngineProvenance,
): JobTagStrictness | undefined {
    switch (jobRequirementStrength) {
        case 'MUST_HAVE_JOB_REQUIREMENT':
        case 'MUST_HAVE_SQ':
            return !shouldHideStrictnessForInferredAttributes || jobProvenance === 'CONFIRMED'
                ? JobTagStrictness.REQUIRED
                : // Returning preferred rather than undefined here to prevent these getting sorted to the end of the list
                  JobTagStrictness.PREFERRED;
        case 'NICE_TO_HAVE_JOB_REQUIREMENT':
        case 'NICE_TO_HAVE_SQ':
            return JobTagStrictness.PREFERRED;
        case 'NONE':
        default:
            return undefined;
    }
}

function isValidFirstOccurrenceOf(
    entity: TaxonomyEntity,
    entities: TaxonomyEntity[],
    idx: number,
): boolean {
    const typeId = getCategoryTypeIdFromRawType(entity?.rawName);
    const isValid =
        !!entity?.displayText?.trim() && typeId && typeId !== YellowLightCategoryType.HIDDEN;
    if (!isValid) {
        return false;
    }

    for (let otherIdx = 0; otherIdx < entities.length; otherIdx++) {
        const other = entities[otherIdx];

        const otherTypeId = getCategoryTypeIdFromRawType(other?.rawName);
        if (entity.displayText === other.displayText && typeId === otherTypeId) {
            return otherIdx === idx;
        }
    }

    return false;
}

function getRequirementStrengthFilterFn(
    shouldOnlyDisplayMustHaveRequirements: boolean,
    shouldHideAttributesWithoutRequirementStrength: boolean,
) {
    if (shouldOnlyDisplayMustHaveRequirements) {
        return (comparison: component.oneGraphMatchComparison.AttributeComparison) =>
            !!comparison.jobRequirementStrength &&
            ['MUST_HAVE_JOB_REQUIREMENT', 'MUST_HAVE_SQ'].includes(
                comparison.jobRequirementStrength,
            );
    } else if (shouldHideAttributesWithoutRequirementStrength) {
        return (comparison: component.oneGraphMatchComparison.AttributeComparison) =>
            !!comparison.jobRequirementStrength && comparison.jobRequirementStrength !== 'NONE';
    } else {
        return () => true;
    }
}

export function translateEntitiesToAttributes({
    taxonomyEntities,
    jobTagReqIdsToStrictness = {},
    taxonomyIdToJssdId = {},
}: Omit<
    GetJobAttributesInput,
    'attributeComparisons' | 'shouldUseMceAttributes' | 'jobSeekerSuidsToFactIds'
>): QualificationAttribute[] {
    return taxonomyEntities
        .filter((req, idx) => isValidFirstOccurrenceOf(req, taxonomyEntities, idx))
        .map(
            ({
                id,
                suid = '',
                uuid: uuidFromEntity = '',
                displayText = '',
                rawName = '',
                inferenceMethod,
                status,
                source,
            }) => {
                const type = getCategoryTypeIdFromRawType(rawName) as YellowLightCategoryType;
                const useSuidForJssdId =
                    inferenceMethod === InferenceMethod.DIRECT &&
                    type !== YellowLightCategoryType.EDUCATION
                        ? suid
                        : '';

                const uuid = uuidFromEntity || id || '';
                return {
                    jssdId:
                        taxonomyIdToJssdId[suid] ?? taxonomyIdToJssdId[uuid] ?? useSuidForJssdId,
                    suid,
                    uuid,
                    label: displayText,
                    sentiment: getSentiment(source, status),
                    type,
                    rawType: rawName.split('/')[0],
                    inferenceMethod,
                    strictness:
                        jobTagReqIdsToStrictness[uuid || id || ''] ||
                        jobTagReqIdsToStrictness[suid],
                };
            },
        );
}

export function translateMCEComparisonsToAttributes(
    {
        attributeComparisons = [],
        taxonomyIdToJssdId = {},
    }: Omit<GetJobAttributesInput, 'taxonomyEntities' | 'shouldUseMceAttributes'>,
    {
        shouldHideAttributesWithoutRequirementStrength,
        shouldHideStrictnessForInferredAttributes,
        shouldOnlyDisplayMustHaveRequirements,
        shouldLimitExpandedChipCount,
    }: FeatureFlagT,
): QualificationAttribute[] {
    return attributeComparisons
        .filter(
            (comparison) =>
                comparison.matchType !== 'JS_ONLY' &&
                !!comparison.attribute?.profileAttributeTypeSuid &&
                !!comparison.attribute?.label,
        )
        .filter(
            getRequirementStrengthFilterFn(
                shouldHideAttributesWithoutRequirementStrength,
                shouldOnlyDisplayMustHaveRequirements,
            ),
        )
        .slice(undefined, shouldLimitExpandedChipCount ? EXPERIMENTAL_CHIP_LIMIT : undefined)
        .map(
            ({
                attribute = {},
                jobProvenance,
                jobRequirementStrength,
                jsProvenance,
                matchType,
            }) => {
                const suid = attribute?.suid ?? '';
                const profileAttributeTypeSuid = attribute?.profileAttributeTypeSuid ?? '';
                const type =
                    customClassIdToYellowLightCategoryType[
                        profileAttributeTypeSuid as keyof typeof customClassIdToYellowLightCategoryType
                    ];
                const useSuidForJssdId =
                    jsProvenance === 'CONFIRMED' && type !== YellowLightCategoryType.EDUCATION
                        ? suid
                        : '';

                return {
                    jssdId: taxonomyIdToJssdId[suid] ?? useSuidForJssdId,
                    suid,
                    label: attribute?.label ?? '',
                    sentiment: getMCESentiment(matchType),
                    type,
                    profileAttributeTypeSuid,
                    rawType: '',
                    inferenceMethod: getInferenceMethod(jsProvenance),
                    strictness: getStrictness(
                        shouldHideStrictnessForInferredAttributes,
                        jobRequirementStrength,
                        jobProvenance,
                    ),
                    uuid: '',
                };
            },
        );
}
